import { useEffect, useState } from 'react'
import {
  CircleStackIcon,
  CloudArrowUpIcon,
  SignalIcon,
} from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'

import { Badge, Button, Card, CardContent, Input, Spinner } from '../../components/ui'
import {
  lagAccrualKeys,
  useLagAccrualConnectors,
  useLagAccrualDataSourceConfig,
  useSaveLagAccrualDataSource,
  useTestLagAccrualDataSource,
} from './api'

export default function ConfigurationPage() {
  const queryClient = useQueryClient()
  const configQuery = useLagAccrualDataSourceConfig()
  const connectorsQuery = useLagAccrualConnectors()
  const saveConfig = useSaveLagAccrualDataSource()
  const testConfig = useTestLagAccrualDataSource()
  const [connectorId, setConnectorId] = useState('')
  const [schema, setSchema] = useState('lag_accruals')

  useEffect(() => {
    if (configQuery.data?.connector_id) {
      setConnectorId(configQuery.data.connector_id)
    }
    if (configQuery.data?.redshift_schema) {
      setSchema(configQuery.data.redshift_schema)
    }
  }, [configQuery.data])

  const canSubmit = Boolean(connectorId && schema.trim())

  const handleSave = async () => {
    await saveConfig.mutateAsync({
      connector_id: connectorId,
      redshift_schema: schema.trim(),
    })
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: lagAccrualKeys.dataSource() }),
      queryClient.invalidateQueries({ queryKey: lagAccrualKeys.run() }),
    ])
  }

  const handleTest = async () => {
    await testConfig.mutateAsync({
      connector_id: connectorId,
      redshift_schema: schema.trim(),
    })
  }

  if (configQuery.isLoading || connectorsQuery.isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const config = configQuery.data
  const connectors = connectorsQuery.data ?? []

  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
      <Card>
        <CardContent className="grid gap-5 p-5">
          <div className="flex items-start gap-3">
            <CircleStackIcon className="mt-0.5 h-5 w-5 text-eliza-red" />
            <div>
              <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">
                Current source
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                Lag Accruals reads normalized EC-02d tables through the platform Redshift connector.
              </p>
            </div>
          </div>

          <div className="grid gap-3 text-sm">
            <div className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-4 py-3 dark:border-dark-border">
              <span className="text-gray-500 dark:text-gray-400">Mode</span>
              <Badge variant="secondary">{config?.mode ?? 'fixture'}</Badge>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-4 py-3 dark:border-dark-border">
              <span className="text-gray-500 dark:text-gray-400">Connector</span>
              <span className="font-medium text-charcoal dark:text-gray-100">
                {config?.connector_name ?? 'Bundled fixtures'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-4 py-3 dark:border-dark-border">
              <span className="text-gray-500 dark:text-gray-400">Schema</span>
              <span className="font-mono text-charcoal dark:text-gray-100">
                {config?.redshift_schema ?? 'lag_accruals'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-5 p-5">
          <div className="flex items-start gap-3">
            <CloudArrowUpIcon className="mt-0.5 h-5 w-5 text-eliza-red" />
            <div>
              <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">
                Redshift connector
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                Select a saved Redshift connector and schema containing the Lag Accruals tables.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_220px]">
            <label className="grid gap-2">
              <span className="text-sm font-medium text-charcoal dark:text-gray-100">
                Connector
              </span>
              <select
                value={connectorId}
                onChange={event => setConnectorId(event.target.value)}
                className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal shadow-sm transition-colors focus:border-eliza-red focus:outline-none focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
              >
                <option value="">Choose connector</option>
                {connectors.map(connector => (
                  <option key={connector.connector_id} value={connector.connector_id}>
                    {connector.connector_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="grid gap-2">
              <span className="text-sm font-medium text-charcoal dark:text-gray-100">
                Schema
              </span>
              <Input value={schema} onChange={event => setSchema(event.target.value)} />
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {connectors.length} Redshift connector{connectors.length === 1 ? '' : 's'} available
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!canSubmit || testConfig.isPending}
                onClick={handleTest}
              >
                {testConfig.isPending ? <Spinner size="sm" /> : <SignalIcon className="h-4 w-4" />}
                Test
              </Button>
              <Button
                type="button"
                variant="brand"
                size="sm"
                disabled={!canSubmit || saveConfig.isPending}
                onClick={handleSave}
              >
                {saveConfig.isPending ? <Spinner size="sm" /> : <CloudArrowUpIcon className="h-4 w-4" />}
                Save
              </Button>
            </div>
          </div>

          {testConfig.data ? (
            <Badge
              className={
                testConfig.data.redshift_connected
                  ? 'w-fit bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'w-fit bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }
            >
              {testConfig.data.message || testConfig.data.status}
            </Badge>
          ) : null}
          {saveConfig.error || testConfig.error ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {(saveConfig.error || testConfig.error)?.message ?? 'Connection request failed.'}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
